<template>
  <div class="stats-chat">
    <div class="chat-header">
      <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
      <span class="text-subtitle-2 font-weight-medium">Claims Assistant</span>
      <v-spacer />
      <span v-if="!loading" class="text-caption text-grey">{{ claims.length }} claims loaded</span>
      <v-progress-circular v-else size="14" width="2" indeterminate color="primary" />
    </div>

    <div ref="messagesEl" class="messages-area">
      <div v-for="(msg, i) in messages" :key="i" :class="['message-row', msg.from]">
        <div v-if="msg.from === 'bot'" class="bot-avatar">
          <v-icon size="16" color="white">mdi-robot-outline</v-icon>
        </div>
        <div :class="['bubble', msg.from]">
          <div v-if="msg.type === 'text'" class="text-body-2" v-html="msg.html || msg.text" />
          <div v-else-if="msg.type === 'table'">
            <div v-if="msg.title" class="text-caption font-weight-bold mb-2" style="color:#5c6bc0">{{ msg.title }}</div>
            <div v-for="row in msg.rows" :key="row.label" class="stat-row">
              <span class="stat-label">
                <v-icon v-if="row.icon" :color="row.iconColor || 'grey'" size="13" class="mr-1">{{ row.icon }}</v-icon>
                {{ row.label }}
              </span>
              <v-chip size="x-small" :color="row.chipColor || 'default'" variant="tonal" class="stat-chip">{{ row.value }}</v-chip>
            </div>
          </div>
        </div>
      </div>

      <div v-if="botTyping" class="message-row bot">
        <div class="bot-avatar"><v-icon size="16" color="white">mdi-robot-outline</v-icon></div>
        <div class="bubble bot typing-dots"><span /><span /><span /></div>
      </div>

      <div v-if="suggestions.length && !botTyping" class="suggestions-row">
        <v-chip v-for="s in suggestions" :key="s" size="small" variant="tonal" color="primary" class="mr-1 mb-1" style="cursor:pointer" @click="sendMessage(s)">{{ s }}</v-chip>
      </div>
    </div>

    <div class="input-area">
      <v-text-field v-model="inputText" variant="outlined" density="compact" hide-details placeholder="Ask about your claims..." bg-color="white" @keydown.enter.prevent="sendMessage(inputText)">
        <template v-slot:append-inner>
          <v-btn icon size="x-small" variant="text" color="primary" :disabled="!inputText.trim()" @click="sendMessage(inputText)">
            <v-icon size="18">mdi-send</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import dayjs from "dayjs";

const WELCOME_SUGGESTIONS = ["Summary", "Ortho claims", "Peer review claims", "Claims by carrier", "Urgent claims", "Claims older than 90 days"];

export default {
  name: "StatsChatFallback",
  data() {
    return {
      inputText: "",
      botTyping: false,
      suggestions: [],
      messages: [{ from: "bot", type: "text", html: "Hi! I can give you a quick snapshot of your outstanding claims. What would you like to know?" }],
    };
  },
  computed: {
    ...mapGetters("osclaims", ["claims", "loading"]),
    claimsByType() {
      const map = {};
      this.claims.forEach((c) => { const t = (c.Type || "Unknown").trim(); map[t] = (map[t] || 0) + 1; });
      return Object.entries(map).sort((a, b) => b[1] - a[1]);
    },
    claimsByCarrier() {
      const map = {};
      this.claims.forEach((c) => { const k = (c.Carrier || "Unknown").trim(); map[k] = (map[k] || 0) + 1; });
      return Object.entries(map).sort((a, b) => b[1] - a[1]);
    },
  },
  watch: {
    claims(val) { if (val.length && this.messages.length === 1) this.suggestions = WELCOME_SUGGESTIONS; },
  },
  mounted() {
    if (this.claims.length) this.suggestions = WELCOME_SUGGESTIONS;
  },
  methods: {
    sendMessage(text) {
      if (!text || !text.trim()) return;
      const q = text.trim();
      this.inputText = "";
      this.suggestions = [];
      this.messages.push({ from: "user", type: "text", text: q });
      this.botTyping = true;
      this.$nextTick(() => this.scrollToBottom());
      setTimeout(() => {
        this.botTyping = false;
        const response = this.processQuery(q);
        this.messages.push(response);
        this.suggestions = response.suggestions || [];
        this.$nextTick(() => this.scrollToBottom());
      }, 500);
    },
    processQuery(q) {
      const lower = q.toLowerCase();
      if (!this.claims.length) return { from: "bot", type: "text", html: "Claims are still loading. Please try again in a moment.", suggestions: ["Summary"] };
      if (this.match(lower, ["summary", "overview", "total", "how many", "all claims"])) return this.buildSummary();
      if (this.match(lower, ["ortho"])) return this.buildTypeReport("ortho");
      if (this.match(lower, ["peer", "p2p", "peer review", "peer-to-peer"])) return this.buildTypeReport("peer");
      if (this.match(lower, ["medical necessity", "medically"])) return this.buildTypeReport("medical");
      if (this.match(lower, ["carrier", "insurance", "payer", "by carrier"])) return this.buildCarrierReport();
      if (this.match(lower, ["urgent", "attention", "overdue", "stale", "follow up", "follow-up"])) return this.buildUrgentReport();
      if (this.match(lower, ["no conv", "unopened", "new claim", "no convo", "not started"])) return this.buildNoConvoReport();
      if (this.match(lower, ["old", "90 day", "age", "aging", "30 day", "60 day"])) return this.buildAgeReport(lower);
      if (this.match(lower, ["type", "breakdown", "category", "categories"])) return this.buildTypeBreakdown();
      if (this.match(lower, ["amount", "dollar", "money", "value", "$"])) return this.buildAmountReport();
      if (this.match(lower, ["active", "open conv", "in progress", "working"])) return this.buildActiveReport();
      if (this.match(lower, ["help", "what can", "what do"])) return this.buildHelp();
      return { from: "bot", type: "text", html: `I didn't quite catch that. Try asking about <b>Ortho claims</b>, <b>peer reviews</b>, <b>carriers</b>, or get a full <b>summary</b>.`, suggestions: WELCOME_SUGGESTIONS };
    },
    match(lower, keywords) { return keywords.some((k) => lower.includes(k)); },
    buildSummary() {
      const total = this.claims.length;
      return { from: "bot", type: "table", title: "Outstanding Claims — Overview", rows: [
        { label: "Total outstanding claims", value: total, icon: "mdi-file-document-multiple", iconColor: "blue-darken-2", chipColor: "blue" },
        { label: "New (no conversation yet)", value: this.claims.filter((c) => c.priority === 2).length, icon: "mdi-alert-decagram", iconColor: "orange-darken-2", chipColor: "orange" },
        { label: "Active conversations", value: this.claims.filter((c) => c.priority === 3 || c.priority === 4).length, icon: "mdi-message-text", iconColor: "green-darken-2", chipColor: "green" },
        { label: "Needs follow-up (stale)", value: this.claims.filter((c) => c.priority === 1).length, icon: "mdi-clock-alert", iconColor: "red-darken-2", chipColor: "red" },
        { label: "Older than 90 days", value: this.claims.filter((c) => dayjs().diff(dayjs(c.DOS), "day") > 90).length, icon: "mdi-calendar-alert", iconColor: "deep-orange", chipColor: "deep-orange" },
        { label: "Carriers involved", value: new Set(this.claims.map((c) => c.Carrier)).size, icon: "mdi-office-building", iconColor: "purple", chipColor: "purple" },
        { label: "Claim types", value: new Set(this.claims.map((c) => c.Type)).size, icon: "mdi-tag-multiple", iconColor: "teal", chipColor: "teal" },
      ], suggestions: ["Claims by type", "Claims by carrier", "Urgent claims", "Claims older than 90 days"] };
    },
    buildTypeReport(keyword) {
      const matched = this.claims.filter((c) => (c.Type || "").toLowerCase().includes(keyword));
      if (!matched.length) {
        const knownTypes = [...new Set(this.claims.map((c) => c.Type).filter(Boolean))].slice(0, 6).join(", ");
        return { from: "bot", type: "text", html: `No claims found matching <b>${keyword}</b>. Known types: <b>${knownTypes || "none"}</b>.`, suggestions: ["Claims by type", "Summary"] };
      }
      const byCarrier = {};
      matched.forEach((c) => { const k = c.Carrier || "Unknown"; byCarrier[k] = (byCarrier[k] || 0) + 1; });
      return { from: "bot", type: "table", title: `${matched.length} claims matching "${keyword}" — by carrier`,
        rows: Object.entries(byCarrier).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([label, value]) => ({ label, value, icon: "mdi-office-building", iconColor: "blue-grey" })),
        suggestions: ["Urgent claims", "Claims by type", "Summary"] };
    },
    buildCarrierReport() {
      return { from: "bot", type: "table", title: `Claims by carrier (top 10)`,
        rows: this.claimsByCarrier.slice(0, 10).map(([label, value], i) => ({ label, value, icon: "mdi-office-building", iconColor: i < 3 ? "blue-darken-2" : "blue-grey", chipColor: i < 3 ? "blue" : "default" })),
        suggestions: ["Ortho claims", "Urgent claims", "Summary"] };
    },
    buildUrgentReport() {
      const urgent = this.claims.filter((c) => c.priority === 1);
      if (!urgent.length) return { from: "bot", type: "text", html: "No stale / needs-follow-up claims right now.", suggestions: ["Summary", "Claims by carrier"] };
      const byCarrier = {};
      urgent.forEach((c) => { const k = c.Carrier || "Unknown"; byCarrier[k] = (byCarrier[k] || 0) + 1; });
      return { from: "bot", type: "table", title: `${urgent.length} claims need follow-up — by carrier`,
        rows: Object.entries(byCarrier).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([label, value]) => ({ label, value, icon: "mdi-clock-alert", iconColor: "red", chipColor: "red" })),
        suggestions: ["No conversation claims", "Claims older than 90 days", "Summary"] };
    },
    buildNoConvoReport() {
      const list = this.claims.filter((c) => c.priority === 2);
      if (!list.length) return { from: "bot", type: "text", html: "All claims have an active conversation — great job!", suggestions: ["Summary"] };
      const byCarrier = {};
      list.forEach((c) => { const k = c.Carrier || "Unknown"; byCarrier[k] = (byCarrier[k] || 0) + 1; });
      return { from: "bot", type: "table", title: `${list.length} claims with no conversation yet — by carrier`,
        rows: Object.entries(byCarrier).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([label, value]) => ({ label, value, icon: "mdi-alert-decagram", iconColor: "orange", chipColor: "orange" })),
        suggestions: ["Urgent claims", "Summary"] };
    },
    buildAgeReport(lower) {
      let days = 90;
      if (lower.includes("30")) days = 30; else if (lower.includes("60")) days = 60; else if (lower.includes("120")) days = 120;
      const aged = this.claims.filter((c) => dayjs().diff(dayjs(c.DOS), "day") > days);
      return { from: "bot", type: "table", title: `${aged.length} claims older than ${days} days — aging buckets`,
        rows: [
          { label: `${days}–${days + 30} days`, value: this.claims.filter((c) => { const a = dayjs().diff(dayjs(c.DOS), "day"); return a > days && a <= days + 30; }).length, icon: "mdi-calendar-clock", iconColor: "deep-orange", chipColor: "deep-orange" },
          { label: `${days + 30}–${days + 90} days`, value: this.claims.filter((c) => { const a = dayjs().diff(dayjs(c.DOS), "day"); return a > days + 30 && a <= days + 90; }).length, icon: "mdi-calendar-clock", iconColor: "deep-orange", chipColor: "deep-orange" },
          { label: `Over ${days + 90} days`, value: this.claims.filter((c) => dayjs().diff(dayjs(c.DOS), "day") > days + 90).length, icon: "mdi-calendar-clock", iconColor: "deep-orange", chipColor: "deep-orange" },
        ], suggestions: ["Urgent claims", "Summary", "Claims by carrier"] };
    },
    buildTypeBreakdown() {
      return { from: "bot", type: "table", title: "Claims by type",
        rows: this.claimsByType.slice(0, 12).map(([label, value]) => ({ label: label || "Unknown", value, icon: "mdi-tag", iconColor: "teal", chipColor: "teal" })),
        suggestions: ["Ortho claims", "Peer review claims", "Summary"] };
    },
    buildActiveReport() {
      return { from: "bot", type: "table", title: `${this.claims.filter((c) => c.priority === 3 || c.priority === 4).length} claims with active conversations`,
        rows: [
          { label: "Updated within last 24h", value: this.claims.filter((c) => c.priority === 4).length, icon: "mdi-message-flash", iconColor: "green", chipColor: "green" },
          { label: "Open (older updates)", value: this.claims.filter((c) => c.priority === 3).length, icon: "mdi-message-text", iconColor: "blue", chipColor: "blue" },
        ], suggestions: ["Urgent claims", "Summary"] };
    },
    buildAmountReport() {
      const withAmount = this.claims.filter((c) => c.Amount && !isNaN(parseFloat(c.Amount)));
      const total = withAmount.reduce((s, c) => s + parseFloat(c.Amount), 0);
      const avg = withAmount.length ? total / withAmount.length : 0;
      const max = withAmount.reduce((m, c) => Math.max(m, parseFloat(c.Amount)), 0);
      return { from: "bot", type: "table", title: "Claim amounts", rows: [
        { label: "Total outstanding value", value: `$${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: "mdi-currency-usd", iconColor: "green", chipColor: "green" },
        { label: "Average claim amount", value: `$${avg.toFixed(2)}`, icon: "mdi-trending-neutral", iconColor: "blue" },
        { label: "Largest single claim", value: `$${max.toFixed(2)}`, icon: "mdi-arrow-up-bold", iconColor: "orange" },
        { label: "Claims with amount data", value: withAmount.length, icon: "mdi-file-check", iconColor: "grey" },
      ], suggestions: ["Summary", "Claims by carrier"] };
    },
    buildHelp() {
      return { from: "bot", type: "text", html: `<b>Overview</b>: "Summary", "How many claims"<br><b>By type</b>: "Ortho claims", "Peer review", "Medical necessity"<br><b>By carrier</b>: "Claims by carrier"<br><b>By status</b>: "Urgent claims", "No conversation", "Active claims"<br><b>By age</b>: "Claims older than 90 days"<br><b>Amounts</b>: "Total claim value"`, suggestions: WELCOME_SUGGESTIONS };
    },
    scrollToBottom() { const el = this.$refs.messagesEl; if (el) el.scrollTop = el.scrollHeight; },
  },
};
</script>

<style scoped>
.stats-chat { display: flex; flex-direction: column; height: 100%; background: #f7f8fc; }
.chat-header { display: flex; align-items: center; padding: 10px 16px; background: white; border-bottom: 1px solid #e0e0e0; flex-shrink: 0; }
.messages-area { flex: 1; overflow-y: auto; padding: 16px 12px; display: flex; flex-direction: column; gap: 10px; }
.message-row { display: flex; align-items: flex-start; gap: 8px; }
.message-row.user { flex-direction: row-reverse; }
.bot-avatar { width: 28px; height: 28px; border-radius: 50%; background: #5c6bc0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
.bubble { max-width: 82%; padding: 10px 14px; border-radius: 14px; font-size: 0.8125rem; line-height: 1.5; }
.bubble.bot { background: white; border: 1px solid #e8eaf6; border-radius: 4px 14px 14px 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
.bubble.user { background: #5c6bc0; color: white; border-radius: 14px 4px 14px 14px; align-self: flex-end; }
.typing-dots { display: flex; align-items: center; gap: 4px; padding: 12px 16px; }
.typing-dots span { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #9e9e9e; animation: bounce 1.2s infinite; }
.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-5px); } }
.stat-row { display: flex; align-items: center; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #f0f0f0; gap: 8px; }
.stat-row:last-child { border-bottom: none; }
.stat-label { font-size: 0.78rem; color: #424242; display: flex; align-items: center; flex: 1; min-width: 0; }
.stat-chip { flex-shrink: 0; }
.suggestions-row { display: flex; flex-wrap: wrap; padding: 4px 0 0 36px; }
.input-area { padding: 10px 12px; background: white; border-top: 1px solid #e0e0e0; flex-shrink: 0; }
</style>
